import React, { useRef } from 'react';
import { testimonials, stats, trustBadges } from '../mock';
import { Star, MapPin, Briefcase, ChevronLeft, ChevronRight, BadgeCheck, Award, ShieldCheck, TrendingUp } from 'lucide-react';
import { SectionHeader } from './Services';

const iconMap = { BadgeCheck, Award, ShieldCheck, TrendingUp };

const Testimonials = () => {
  const scrollerRef = useRef(null);

  const scroll = (dir) => {
    if (!scrollerRef.current) return;
    const amount = scrollerRef.current.clientWidth * 0.7;
    scrollerRef.current.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  return (
    <section id="testimonials" className="relative py-20 md:py-28">
      <div className="squiggle-bg" />
      <div className="max-w-7xl mx-auto px-6 relative">
        <SectionHeader
          eyebrow="Testimonials"
          title={<>What Our Loving <em className="italic">Clients</em> Say</>}
          description="We’ve helped businesses of all sizes unlock growth, refine their strategies, and achieve lasting results. Here’s what our clients say."
        />

        <div className="relative">
          <div ref={scrollerRef} className="flex gap-5 overflow-x-auto hide-scrollbar scroll-smooth pb-2 -mx-6 px-6">
            {testimonials.map((t) => (
              <div key={t.id} className="testimonial-card min-w-[85%] sm:min-w-[520px] max-w-[520px] p-6 md:p-7 flex-shrink-0">
                <div className="flex items-center gap-4 mb-5">
                  <img src={t.image} alt={t.name} className="w-14 h-14 rounded-full object-cover" />
                  <div>
                    <h4 className="font-body font-semibold text-[#143324] text-lg">{t.name}</h4>
                    <p className="text-[13px] text-[#3d3d3d] font-body">{t.role}</p>
                  </div>
                </div>
                <p className="text-[#1c1c1c] text-[15px] leading-relaxed font-body mb-6">
                  “{t.quote}”
                </p>
                <div className="flex items-center justify-between pt-5 border-t border-[#143324]/10">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="inline-flex items-center gap-1.5 text-[12px] text-[#3d3d3d] font-body">
                      <MapPin size={13} className="text-[#143324]" /> {t.location}
                    </div>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#143324]/15 text-[12px] text-[#143324] font-body">
                      <Briefcase size={12} /> {t.type}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={13} className={i < t.rating ? 'star-filled' : 'star-empty'} fill="currentColor" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-center gap-3">
            <button onClick={() => scroll(-1)} className="w-10 h-10 rounded-full border border-[#143324]/25 flex items-center justify-center hover:bg-[#143324]/5 transition-colors">
              <ChevronLeft size={18} className="text-[#143324]" />
            </button>
            <button onClick={() => scroll(1)} className="w-10 h-10 rounded-full border border-[#143324]/25 flex items-center justify-center hover:bg-[#143324]/5 transition-colors">
              <ChevronRight size={18} className="text-[#143324]" />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-14 card-soft rounded-2xl p-8 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 text-center">
            {stats.map((s, i) => (
              <div key={i} className={`px-4 ${i < stats.length - 1 ? 'md:border-r border-[#143324]/10' : ''}`}>
                <div className="font-serif-display text-[#143324] text-5xl md:text-6xl leading-none mb-2">
                  {s.value}
                </div>
                <p className="text-[13px] text-[#3d3d3d] font-body">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Trust badges */}
        <div className="mt-14 border-t border-[#143324]/10 pt-8">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {trustBadges.map((b, i) => {
              const Icon = iconMap[b.icon];
              return (
                <div key={i} className="flex items-center gap-2 text-[#1c1c1c]">
                  {Icon && <Icon size={18} className="text-[#143324]" strokeWidth={1.6} />}
                  <span className="text-sm font-body">{b.label}</span>
                  {i < trustBadges.length - 1 && <span className="hidden md:inline text-[#143324]/25 ml-6">|</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
